import React, { useEffect, useState } from "react";
import styled from "styled-components";
import DayLabel from "./DayLabel";
import CalendarDay from "./CalendarDay";
import CalendarControls from "./CalendarControls";
import dayjs from "dayjs";

export type CalendarEvent = {
  type: string;
  start: dayjs.Dayjs;
  color: string;
};

export type CalendarDayInfo = {
  number: number;
  type: string;
  events: CalendarEvent[];
  onClick?: () => void;
  year: number;
  month: number;
  selectedMonth: number;
  selectedDay: dayjs.Dayjs;
};

export type CalendarEventResponse = {
  loading: boolean;
  data: CalendarEvent[];
};

type CalendarProps = {
  defaultMonth: dayjs.Dayjs;
  handleMonthChange?: (start: dayjs.Dayjs, end: dayjs.Dayjs) => void;
  events: CalendarEventResponse;
  handleDayClick?: (day: dayjs.Dayjs) => void;
};

const CalendarWrapper = styled.div.attrs({ className: "calendar-grid-wrapper" })`
  display: grid;
  grid-template-rows: 2rem repeat(5, 1fr);
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
`;

const CalendarContainer = styled.div.attrs({ className: "calendar-container" })`
  padding: 0.5rem;
  background-color: #f5f5f4;
  width: 40.25rem;
  border-radius: 0.625rem;
  border: #afafb1;
  background: #f5f5f4;
  border: 1px solid #afafb1;
`;

const Calendar: React.FC<CalendarProps> = ({ defaultMonth, handleMonthChange, events, handleDayClick }) => {
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(defaultMonth);
  const [selectedDay, setSelectedDay] = useState<dayjs.Dayjs>(dayjs());
  const [calendarDays, setCalendarDays] = useState<CalendarDayInfo[]>([]);
  const [calendarDaysWithEvents, setCalendarDaysWithEvents] = useState<CalendarDayInfo[]>([]);

  const handleDayClickInternal = (clickedDay: dayjs.Dayjs) => {
    // For now the selected day will always be the current day. However, in the future we will want to make the selected day interactable. This is where we will do that.
    // setSelectedDay(clickedDay);

    if (handleDayClick) {
      handleDayClick(clickedDay);
    }
  };

  const handleLeftArrowClick = () => {
    const newMonth = selectedMonth.subtract(1, "month");
    setSelectedMonth(newMonth);
  };

  const handleRightArrowClick = () => {
    const newMonth = selectedMonth.add(1, "month");
    setSelectedMonth(newMonth);
  };

  const getWeeksInMonth = (year: number, month: number) => {
    const firstDayOfMonth = dayjs(`${year}-${month + 1}-01`).day();
    const daysInMonth = dayjs(`${year}-${month + 1}`).daysInMonth();
    const daysInFirstWeek = 7 - firstDayOfMonth;
    const totalDaysWithoutFirstWeek = daysInMonth - daysInFirstWeek;
    const totalWeeks = Math.ceil(totalDaysWithoutFirstWeek / 7) + 1;

    return totalWeeks;
  };

  const getEventsForDay = (year: number, month: number, day: number): CalendarEvent[] => {
    return events.data.filter((event) => {
      const eventYear = event.start.year();
      const eventMonth = event.start.month();
      const eventDay =  event.start.date();

      return eventYear === year && eventMonth === month && eventDay === day;
    });
  };

  useEffect(() => {
    const year = selectedMonth.year();
    const month = selectedMonth.month();

    const daysInMonth = selectedMonth.daysInMonth();

    const firstDayOfMonth = selectedMonth.day();

    const daysArray = Array.from({ length: daysInMonth }, (_, index) => ({
      number: index + 1,
      type: "default",
      events: [],
      year: year,
      month: month,
    }));

    const previousMonthDaysArray = Array.from({ length: firstDayOfMonth }, (_, index) => ({
      number: selectedMonth.subtract(1, "month").daysInMonth() - firstDayOfMonth + index + 1,
      type: "secondary",
      events: [],
      year: month === 0 ? year - 1 : year,
      month: month === 0 ? 11 : month - 1,
    }));

    const totalWeeks = getWeeksInMonth(year, month);
    const totalCalendarCells = totalWeeks * 7;

    const remainingDays = totalCalendarCells - daysArray.length - previousMonthDaysArray.length;
    const nextMonthDaysArray = Array.from({ length: remainingDays }, (_, index) => ({
      number: index + 1,
      type: "seconday",
      events: [],
      year: month === 11 ? year + 1 : year,
      month: month === 11 ? 0 : month + 1,
    }));

    const combinedDays = [...previousMonthDaysArray, ...daysArray, ...nextMonthDaysArray];

    const startDate = combinedDays.length > 0 ? dayjs(`${combinedDays[0].year}-${combinedDays[0].month + 1}-${combinedDays[0].number}`) : null;
    const endDate =
      combinedDays.length > 0 ? dayjs(`${combinedDays[combinedDays.length - 1].year}-${combinedDays[combinedDays.length - 1].month + 1}-${combinedDays[combinedDays.length - 1].number}`) : null;

    if (handleMonthChange && startDate && endDate) {
      const adjustedEndDate = endDate.endOf("day");
      handleMonthChange(startDate, adjustedEndDate);
    }

    setCalendarDays(combinedDays.map((day) => ({ ...day, selectedMonth: selectedMonth.month(), selectedDay })));
  }, [selectedMonth]);

  useEffect(() => {
    if (events.loading) {
      return;
    }

    const daysWithEvents = calendarDays.map((day) => ({
      ...day,
      events: getEventsForDay(day.year, day.month, day.number),
    }));

    setCalendarDaysWithEvents(daysWithEvents);
  }, [JSON.stringify(events), JSON.stringify(calendarDays)]);

  return (
    <CalendarContainer>
      <CalendarControls leftArrowClick={handleLeftArrowClick} rightArrowClick={handleRightArrowClick} selectedMonth={selectedMonth} />
      <CalendarWrapper>
        <DayLabel label="Sun" />
        <DayLabel label="Mon" />
        <DayLabel label="Tue" />
        <DayLabel label="Wed" />
        <DayLabel label="Thu" />
        <DayLabel label="Fri" />
        <DayLabel label="Sat" />

        {calendarDaysWithEvents.map((day, index) => (
          <CalendarDay
            key={index}
            number={day.number}
            type={day.type}
            events={day.events}
            year={day.year}
            month={day.month}
            selectedMonth={selectedMonth.month()}
            onClick={() => {
              const clickedDay = dayjs(`${day.year}-${day.month + 1}-${day.number}`);
              handleDayClickInternal(clickedDay);
            }}
            selectedDay={selectedDay}
          />
        ))}
      </CalendarWrapper>
    </CalendarContainer>
  );
};

export default Calendar;
